// ============================================================================
// authVedett.js — Callable Function hívások védelme a bejelentkezési tokent
// érintő hibák ellen (2026-10-03, tagi jelzésre Androidról: a küldés
// "(unauthenticated)" hibakóddal bukott, és csak ki-/belépés után ment).
//
// A HIBA: a hívás a Firebase SDK-tól kapja a bejelentkezési tokent. Ha az
// alkalmazás háttérből tér vissza (Androidon gyakori), a token lejárt lehet,
// és a frissítése - hálózat híján, vagy mert az eszköz épp "felébred" -
// elbukik. Az SDK ilyenkor TOKEN NÉLKÜL küldi el a kérést, a szerver pedig
// "unauthenticated"-del utasítja el. Eddig ez a felhasználónak általános
// "Nem sikerült elküldeni" volt, ami újrapróbálva sem javult.
//
// MOST:
//  1. ELŐELLENŐRZÉS: ha nincs bejelentkezett felhasználó, vagy a token
//     lekérése elhasal, a hívás ki sem megy - pontos okkal.
//  2. Ha a szerver mégis "unauthenticated"-et ad: kényszerített
//     tokenfrissítés és EGY automatikus újrapróbálkozás. Biztonságos: az
//     elutasított kérést a szerver nem dolgozta fel, és minden hívó
//     idempotens azonosítót használ (requestId / clientMessageId).
//  3. Ha a frissítés is elbukik: "app/session-expired" hibakódú hiba (az
//     `authCode` mezőben a pontos Firebase-kóddal) - a felület ebből tudja,
//     hogy újra be kell jelentkezni. Tisztán hálózati hibánál az EREDETI
//     hiba megy tovább ("megszakadt" kezelés, az azonosító megmarad).
// ============================================================================

function lejart(authCode) {
  const e = new Error("A bejelentkezés lejárt vagy megszakadt.");
  e.code = "app/session-expired";
  e.authCode = authCode;
  return e;
}

const HALOZAT = "auth/network-request-failed";

// Rövid várakozások a hálózat "felébredéséhez" (ezredmásodperc). Push-értesítés
// után, vagy háttérből visszatérve az eszköz hálózata az első pillanatokban
// gyakran még nem érhető el: a tokenfrissítés ilyenkor elsőre elbukik, pár
// tized másodperc múlva viszont sikerül (2026-10-03, tagi jelzés: "jött egy
// push, majd a küldés unauthenticated hibával bukott").
export const HALOZAT_VARAKOZAS = [400, 1200, 2500];

const alszik = (ms) => new Promise((r) => setTimeout(r, ms));

/** Egy művelet újrapróbálása, amíg a hiba tisztán hálózati. Más hibát azonnal továbbad. */
async function halozatiHibaraUjra(muvelet, varakozasok, alvas) {
  let utolso;
  for (let i = 0; i <= varakozasok.length; i++) {
    try {
      return await muvelet();
    } catch (e) {
      utolso = e;
      if (!e || String(e.code || "") !== HALOZAT || i === varakozasok.length) throw e;
      await alvas(varakozasok[i]);
    }
  }
  throw utolso;
}

/**
 * @param {{currentUser: object|null}} auth
 * @param {(payload: any) => Promise<any>} callable
 * @param {() => Promise<{ok: boolean, code?: string}>} frissitsdATokent
 * @param {{varakozasok?: number[], alvas?: (ms:number)=>Promise<void>}} [opciok] tesztekhez
 */
export function vedettHivas(auth, callable, frissitsdATokent, opciok = {}) {
  const varakozasok = opciok.varakozasok || HALOZAT_VARAKOZAS;
  const alvas = opciok.alvas || alszik;
  return async (payload) => {
    if (!auth.currentUser) throw lejart("app/no-current-user");
    try {
      // Friss, ha kell; gyorsítótárból, ha nem. Hálózati hibánál rövid
      // várakozással újrapróbáljuk (lásd HALOZAT_VARAKOZAS).
      await halozatiHibaraUjra(() => auth.currentUser.getIdToken(), varakozasok, alvas);
    } catch (e) {
      const kod = String((e && e.code) || "");
      if (kod === HALOZAT) throw e; // végig hálózat: nem bejelentkezési probléma
      throw lejart(kod || "auth/unknown");
    }
    try {
      return await callable(payload);
    } catch (err) {
      if (!err || err.code !== "functions/unauthenticated") throw err;
      let fr = await frissitsdATokent();
      for (let i = 0; !fr.ok && fr.code === HALOZAT && i < varakozasok.length; i++) {
        await alvas(varakozasok[i]);
        fr = await frissitsdATokent();
      }
      if (!fr.ok) {
        if (fr.code === HALOZAT) throw err;
        throw lejart(fr.code);
      }
      return await callable(payload); // egyetlen automatikus újrapróbálkozás
    }
  };
}
